import { queryOptions, useQuery } from "@tanstack/react-query";
import * as z from "zod";
import { api } from "@/lib/http";

export const POLICY_TYPES = ["Blocklist", "Redactlist"] as const;

export const policyTypeSchema = z.enum(POLICY_TYPES);

export const policyFormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  priority: z.coerce.number().min(-20).max(20).default(0),
  type: policyTypeSchema,
  is_pre_ready: z.boolean().default(false),
  is_active: z.boolean().default(true),
  entities: z.array(z.number().int()).optional(),
});

export const policySchema = policyFormSchema.extend({
  id: z.number(),
  created_at: z.string(),
  updated_at: z.string().nullable(),
  created_by_id: z.uuid().nullable().optional(),
  created_by: z
    .object({
      id: z.uuid(),
      full_name: z.string(),
      email: z.email(),
    })
    .nullable()
    .optional(),
});

export const policiesListSchema = z.object({
  count: z.number(),
  items: z.array(policySchema),
  next_page: z.string().nullable(),
  previous_page: z.string().nullable(),
});

export const policyUsersSchema = z.array(
  z.object({
    id: z.uuid(),
    full_name: z.string(),
    email: z.email(),
    is_active: z.boolean(),
    is_verified: z.boolean(),
    added_at: z.string(),
  }),
);

export const policyGroupsSchema = z.array(
  z.object({
    id: z.number(),
    name: z.string(),
    description: z.string().nullable().optional(),
    added_at: z.string(),
  }),
);

export const policyEntitiesSchema = z.array(
  z.object({
    id: z.number(),
    name: z.string(),
    description: z.string().nullable().optional(),
    added_at: z.string(),
  }),
);

export type PolicyType = z.infer<typeof policyTypeSchema>;
export type PolicyFormData = z.infer<typeof policyFormSchema>;
export type Policy = z.infer<typeof policySchema>;
export type PoliciesList = z.infer<typeof policiesListSchema>;
export type PolicyUsers = z.infer<typeof policyUsersSchema>;
export type PolicyGroups = z.infer<typeof policyGroupsSchema>;
export type PolicyEntities = z.infer<typeof policyEntitiesSchema>;

type SearchParams = {
  page: number;
  per_page?: number;
  search?: string;
  priority?: number;
  policy_type?: PolicyType;
  is_active?: boolean;
};

export function policiesListQueryOptions(params: SearchParams) {
  return queryOptions({
    queryKey: ["policies", "list", params],
    queryFn: async () => {
      const sp = new URLSearchParams({
        page: String(params.page),
        per_page: String(params.per_page ?? 10),
      });
      if (params.search) sp.set("search", params.search);
      if (params.priority !== undefined)
        sp.set("priority", String(params.priority));
      if (params.policy_type) sp.set("policy_type", params.policy_type);
      if (params.is_active !== undefined)
        sp.set("is_active", String(params.is_active));

      const data = await api.raw
        .get("api/v1/policies/paginated", { searchParams: sp })
        .json<PoliciesList>();
      return data;
    },
  });
}
export function allPoliciesQueryOptions(params?: {
  search?: string;
  order?: "asc" | "desc";
}) {
  return queryOptions({
    queryKey: ["policies", "list", "all", params],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params?.search) searchParams.set("search", params.search);
      if (params?.order) searchParams.set("order", params.order);

      const data = await api.raw
        .get("api/v1/policies/", { searchParams })
        .json<Array<Policy>>();
      return data;
    },
  });
}

export function usePoliciesList(params: SearchParams) {
  return useQuery(policiesListQueryOptions(params));
}

export function policyQueryOptions(id: number) {
  return queryOptions({
    queryKey: ["policies", "detail", id],
    queryFn: () => api.raw.get(`api/v1/policies/${id}`).json<Policy>(),
  });
}
export function usePolicy(id: number) {
  return useQuery(policyQueryOptions(id));
}

export function policyUsersQueryOptions(policyId: number) {
  return queryOptions({
    queryKey: ["policies", "users", policyId],
    queryFn: async () =>
      api.raw.get(`api/v1/policies/${policyId}/users`).json<PolicyUsers>(),
  });
}
export function usePolicyUsers(policyId: number) {
  return useQuery(policyUsersQueryOptions(policyId));
}

export function policyGroupsQueryOptions(policyId: number) {
  return queryOptions({
    queryKey: ["policies", "groups", policyId],
    queryFn: async () =>
      api.raw.get(`api/v1/policies/${policyId}/groups`).json<PolicyGroups>(),
  });
}
export function usePolicyGroups(policyId: number) {
  return useQuery(policyGroupsQueryOptions(policyId));
}

export function policyEntitiesQueryOptions(policyId: number) {
  return queryOptions({
    queryKey: ["policies", "entities", policyId],
    queryFn: async () =>
      api.raw
        .get(`api/v1/policies/${policyId}/entities`)
        .json<PolicyEntities>(),
  });
}
export function usePolicyEntities(policyId: number) {
  return useQuery(policyEntitiesQueryOptions(policyId));
}
